
import React, { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { sanitizeInput, detectSQLInjection, validateEmail } from '@/utils/securityUtils';
import { AlertTriangle } from 'lucide-react';

interface SecureInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'password' | 'textarea';
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  validateEmail?: boolean;
  preventSQLInjection?: boolean;
}

const SecureInput: React.FC<SecureInputProps> = ({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  maxLength = 1000,
  required = false,
  disabled = false,
  className = '',
  validateEmail: shouldValidateEmail = false,
  preventSQLInjection = true,
}) => {
  const [error, setError] = useState<string>('');
  const [isValid, setIsValid] = useState(true);

  const handleChange = useCallback((inputValue: string) => {
    let sanitizedValue = inputValue;
    let errorMessage = '';
    let valid = true;

    // Sanitize input
    sanitizedValue = sanitizeInput(inputValue, maxLength);

    // Check for SQL injection if enabled
    if (preventSQLInjection && detectSQLInjection(sanitizedValue)) {
      errorMessage = 'Input contains potentially harmful content';
      valid = false;
    }

    // Email validation if enabled
    if (shouldValidateEmail && type === 'email' && sanitizedValue && !validateEmail(sanitizedValue)) {
      errorMessage = 'Please enter a valid email address';
      valid = false;
    }

    // Required field validation
    if (required && !sanitizedValue.trim()) {
      errorMessage = 'This field is required';
      valid = false;
    }

    setError(errorMessage);
    setIsValid(valid);
    onChange(sanitizedValue);
  }, [onChange, maxLength, preventSQLInjection, shouldValidateEmail, type, required]);

  const inputProps = {
    id,
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange(e.target.value),
    placeholder,
    disabled,
    className: `${className} ${!isValid ? 'border-red-500' : ''}`,
    maxLength,
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      
      {type === 'textarea' ? (
        <Textarea {...inputProps} />
      ) : (
        <Input {...inputProps} type={type} />
      )}
      
      {error && (
        <div className="flex items-center space-x-2 text-red-600 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
      
      {maxLength && (
        <div className="text-xs text-gray-500 text-right">
          {value.length}/{maxLength} characters
        </div>
      )}
    </div>
  );
};

export default SecureInput;
